import { Locale } from 'vue-i18n';
import { requestWithAuthorization } from './requestWithAuthorization';
import { getIcon, addParameterToUrl } from '@/appsync/service/GraphqlUtils';
import PortalConfigure from '@/portal-configure';
import {
  LoginInfo,
  ProductMenu,
  Product,
  ErrorInfo,
  ProductAdditionalQueryMap,
} from '@/types/type';
import { fixedEncodeURIComponent } from '@/utils/CommonUtils';

export interface SearchParam extends LoginInfo {
  searchKey: string;
  language: Locale;
}

interface SearchResultMenuType {
  product: Product;
  menuId: string;
  name: string | null;
  description: string | null;
  url: string;
  isFavorite: boolean;
  menuIconFont: string | null;
  canUseFromExtra: boolean;
}

const buildUrl = (param: SearchParam) => {
  const paramString = Object.entries(param)
    .sort()
    .map(([key, value]) => `${key}=${fixedEncodeURIComponent(value)}`)
    .join('&');
  return `${PortalConfigure.API.SearchMenu}?${paramString}`;
};

const toProductMenu = (
  menu: SearchResultMenuType,
  productAdditionalQueryMap?: ProductAdditionalQueryMap
): ProductMenu => {
  const icon = getIcon(menu.menuIconFont);
  const product = menu.product;
  const url = addParameterToUrl(menu.url, product, productAdditionalQueryMap);
  return {
    ...menu,
    id: menu.menuId,
    title: menu.name || '',
    description: menu.description || '',
    menuType: 'menu',
    available: true,
    icon,
    product,
    url,
  };
};

export const fetchSearchResult = async (
  param: SearchParam,
  productAdditionalQueryMap: ProductAdditionalQueryMap
): Promise<ProductMenu[]> => {
  const url = buildUrl(param);
  let error: Error = { name: '', message: '' };
  const response = await requestWithAuthorization.get(url).catch((e: Error) => {
    error = e;
  });
  if (error && error.message) {
    const errorInfo: ErrorInfo = {
      code: 'E00001',
      title: 'Error in fetch search result',
      message: error.message,
      args: param,
    };
    error.message = JSON.stringify(errorInfo);
    throw error;
  }
  if (response && response.data && response.data.length > 0) {
    const data: SearchResultMenuType[] = response.data;
    return data.map(menu => toProductMenu(menu, productAdditionalQueryMap));
  }
  return [];
};
